import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core'
import { useContext, useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { updateProject } from '../../../actions/projectActions'
import pdf from '../../../assets/pdf/example.pdf'
import {
  PROJECT_CURRENT_STEP,
  PROJECT_EXIT_REQUEST,
  PROJECT_UPDATE_RESET,
} from '../../../constants/projectConstants'
import { useInput } from '../../../hooks/useInput'
import { Context } from '../../../providers/ProjectContextProvider'
import FileUploadTable from '../../Form/FileUploadTable'
import FormContainer from '../../Form/FormContainer'
import MultiFilesInput from '../../Form/MultiFilesInput'
import TextAreaInput from '../../Form/TextAreaInput'

interface ReportRequirementsProps {
  resetRequestValues: () => void
  selectedPartId: string
  selectedMachineId: string
  selectedPrograms: Program[]
  qty: number | undefined
  hasProgram: string | boolean | undefined
  keepProgram: string | boolean | undefined
  notes: string | boolean | undefined
  documents: string[]
}

const ReportRequirements = ({
  resetRequestValues,
  selectedPartId,
  selectedMachineId,
  selectedPrograms,
  qty,
  hasProgram,
  keepProgram,
  notes,
  documents,
}: ReportRequirementsProps) => {
  const dispatch = useDispatch()

  const { setShowNotifyUser } = useContext(Context)

  // eslint-disable-next-line no-unused-vars
  const [saveAndExit, setSaveAndExit] = useState(false)
  const [files, setFiles] = useState([])
  const [filesUploading, setFilesUploading] = useState([])
  const [filesError, setFilesError] = useState('')
  const [stayOnPage, setStayOnPage] = useState(false)

  const {
    value: comments,
    bind: bindComments,
    error: commentsError,
    setError: setCommentsError,
  } = useInput('')
  const [reportType, setReportType] = useState('Standard')

  const { loading: loadingUpdate, success: successUpdate } = useSelector(
    (state: RootStateOrAny) => state.projectUpdate
  )
  const { project } = useSelector(
    (state: RootStateOrAny) => state.projectDetails
  )
  const { userInfo } = useSelector((state: RootStateOrAny) => state.userLogin)

  useEffect(() => {
    if (successUpdate) {
      resetRequestValues()
      dispatch({ type: PROJECT_UPDATE_RESET })

      if (saveAndExit) {
        dispatch({ type: PROJECT_EXIT_REQUEST })
      } else if (stayOnPage) {
        setStayOnPage(false)
      } else {
        dispatch({
          type: PROJECT_CURRENT_STEP,
          payload: 5,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successUpdate, dispatch, resetRequestValues, saveAndExit])

  const formIsReady = () => {
    let ready = true
    if (reportType !== 'Standard' && comments === '') {
      setCommentsError('Please enter your customization requirements.')
      ready = false
    }

    return ready
  }

  const submitHandler = () => {
    if (formIsReady()) {
      addRequestToProject()
    }
  }

  const addRequestToProject = () => {
    let projectClone = JSON.parse(JSON.stringify(project)) // deep cloning

    if (project.type === 'programming') {
      let request = {
        type: 'programming',
        part: selectedPartId,
        machine: selectedMachineId,
        report: { reportType, comments, files },
      }
      projectClone.requests.push(request)
    }

    if (project.type === 'inspection' && !hasProgram) {
      let request = {
        type: 'combined',
        part: selectedPartId,
        machine: selectedMachineId,
        programDetails: {
          programs: [],
          hasProgram,
          keepProgram,
          notes,
          documents,
          qty,
        },
        report: { reportType, comments, files },
      }
      projectClone.requests.push(request)
    }

    if (project.type === 'inspection' && hasProgram) {
      let selectedProgramIds = []

      for (let i = 0; i < selectedPrograms.length; i++) {
        selectedProgramIds.push(selectedPrograms[i]._id)
      }

      let request = {
        type: 'inspection',
        part: selectedPartId,
        machine: selectedMachineId,
        programDetails: {
          programs: selectedProgramIds,
          hasProgram,
          keepProgram,
          notes,
          documents,
          qty,
        },
        report: { reportType, comments, files },
      }

      projectClone.requests.push(request)
    }

    dispatch(updateProject(projectClone))
  }

  const handleSaveAndExit = () => {
    if (userInfo.isAdmin) {
      setStayOnPage(true)
      setShowNotifyUser(true)
      addRequestToProject()
    } else {
      addRequestToProject()
    }
  }

  return (
    <>
      <FormContainer>
        <div className="m-b-30">
          <h4>
            Report requirements{' '}
            {userInfo.isAdmin && project.userName && `for ${project.userName}`}
          </h4>
        </div>

        <FormControl component="fieldset">
          <RadioGroup
            aria-label="report type"
            name="reportType"
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
          >
            <FormControlLabel
              value="Standard"
              control={<Radio />}
              label={
                <div>
                  Standard report.{' '}
                  <a
                    className="link"
                    href={pdf}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    See example.
                  </a>
                </div>
              }
            />
            <FormControlLabel
              value="Partial Custom"
              control={<Radio />}
              label="Partial customization (enter requirements below in comment field)"
            />
            <FormControlLabel
              value="Full Custom"
              control={<Radio />}
              label="Full customization (enter requirements below in comment field)"
            />
          </RadioGroup>
        </FormControl>

        <TextAreaInput
          name="comments"
          label="Comments"
          bindValue={bindComments}
          rows={4}
          error={commentsError}
          className="m-b-16"
        />

        <MultiFilesInput
          name="files"
          label="Files"
          filesUploading={filesUploading}
          setFiles={setFiles}
          setFilesUploading={setFilesUploading}
          contentTop={
            <>
              Drag and drop or click to add
              <strong> reference files</strong>
            </>
          }
          contentBottom="Please include header, footer, and logo files if customization required"
          error={filesError}
          setError={setFilesError}
          disabled={false}
        />

        {(files.length > 0 || filesUploading.length > 0) && (
          <FileUploadTable
            data={files}
            setData={setFiles}
            filesUploading={filesUploading}
          />
        )}

        <div className="actions m-t-40">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveAndExit}
          >
            Save and exit
          </Button>

          <div>
            <Button
              disabled={loadingUpdate}
              variant="outlined"
              color="primary"
              onClick={() =>
                dispatch({
                  type: PROJECT_CURRENT_STEP,
                  payload: project.type === 'inspection' ? '3b' : 3,
                })
              }
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              style={{ marginLeft: 20 }}
              onClick={submitHandler}
            >
              Next
            </Button>
          </div>
        </div>
      </FormContainer>
    </>
  )
}

export default ReportRequirements
